import {Button, Col, Form, Row, Table} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {FormikErrors, FormikTouched} from 'formik'
import {DateInput} from 'app/tfi/components/DateInput'
import {TFormAllValues} from '../../../policies/policiesTypes'
import {INIT_FORM_LIFE_MEDICATION} from '../../../../helpers/consts'

interface MedicalTabProps {
  onPrevStep: () => void
  onNextStep: () => void
  allValues: TFormAllValues
  errors: FormikErrors<TFormAllValues>
  touched: FormikTouched<TFormAllValues>
  handleChange: (field: string, value: any) => void
  handleBlur: (field: string) => void
  /**
   * Se recibe por consistencia con las demás pestañas, pero no se aplica: hoy ninguna
   * (PolicyHolderTab, FamilyMembersTab, ExtraTab) bloquea sus campos por status, y
   * hacerlo sólo acá dejaba la pestaña médica de sólo lectura fuera de PENDING.
   */
  isDisableEdit?: boolean
}

/**
 * Sección D del Pre-Application Form de Vida.
 *
 * Estatura, peso y el sí/no de tabaco viven en el holder (`Member_policy`), no en
 * `Policy_life_detail` — se editan desde acá pero se guardan con el miembro.
 */
export const MedicalTab: React.FC<MedicalTabProps> = ({
  onPrevStep,
  onNextStep,
  allValues,
  handleChange,
  handleBlur,
  isDisableEdit = false,
}) => {
  const intl = useIntl()
  const life = allValues.policyLifeDetail
  const holder = allValues.holder
  const medications = allValues.policyLifeMedications || []

  const onLife = (field: string, value: any) => handleChange(`policyLifeDetail.${field}`, value)
  const onLifeBlur = (field: string) => handleBlur(`policyLifeDetail.${field}`)

  const addMedication = () =>
    handleChange('policyLifeMedications', [...medications, {...INIT_FORM_LIFE_MEDICATION}])

  const removeMedication = (index: number) => {
    const target = medications[index]
    // Las filas que ya existen en la base se marcan para borrado; las nuevas se descartan.
    if (target?.id) {
      handleChange('policyLifeMedicationsDelete', [
        ...(allValues.policyLifeMedicationsDelete || []),
        target.id,
      ])
    }
    handleChange(
      'policyLifeMedications',
      medications.filter((_, i) => i !== index)
    )
  }

  const usesTobacco = !!holder?.usesTobacco

  return (
    <div className='policy-insurance-members-container'>
      <div className='kt-heading kt-heading--md custom-border-brand'>
        <FormattedMessage
          id='policies.detail.form.life.medicalHeading'
          defaultMessage='Medical information'
        />
      </div>

      {/* Estatura y peso se editan en Policy holder → Specific information, que es su
          dueño para todos los tipos de póliza. Acá se muestran sólo como contexto para
          no tener dos lugares donde tocar el mismo campo. */}
      <div className='text-muted small mb-3'>
        <i className='bi bi-info-circle me-1' />
        <FormattedMessage
          id='policies.detail.form.life.heightWeightRef'
          defaultMessage='Height: {height} · Weight: {weight} — edit them in Policy holder › Specific information'
          values={{
            height: holder?.height || '—',
            weight: holder?.weight || '—',
          }}
        />
      </div>

      {/* Medicamentos: reemplaza el campo libre `medicinesNeeded` de Extras en Vida. */}
      <div className='kt-heading kt-heading--md custom-border-brand mt-5'>
        <FormattedMessage
          id='policies.detail.form.life.medicationsHeading'
          defaultMessage='Medications'
        />
      </div>
      <Table responsive bordered size='sm' className='align-middle'>
        <thead>
          <tr>
            <th>
              <FormattedMessage
                id='policies.detail.form.life.medicationName'
                defaultMessage='Medication'
              />
            </th>
            <th>
              <FormattedMessage id='policies.detail.form.life.dosage' defaultMessage='Dosage' />
            </th>
            <th>
              <FormattedMessage
                id='policies.detail.form.life.medicationFrequency'
                defaultMessage='Frequency'
              />
            </th>
            <th>
              <FormattedMessage
                id='policies.detail.form.life.diagnosis'
                defaultMessage='Reason / diagnosis'
              />
            </th>
            <th style={{width: 48}} />
          </tr>
        </thead>
        <tbody>
          {medications.length === 0 && (
            <tr>
              <td colSpan={5} className='text-center text-muted py-3'>
                <FormattedMessage
                  id='policies.detail.form.life.noMedications'
                  defaultMessage='No medications registered'
                />
              </td>
            </tr>
          )}
          {medications.map((medication, index) => (
            <tr key={medication.id || `new-${index}`}>
              <td>
                <Form.Control
                  size='sm'
                  value={medication.name || ''}
                  onChange={(e) =>
                    handleChange(`policyLifeMedications[${index}].name`, e.target.value)
                  }
                />
              </td>
              <td>
                <Form.Control
                  size='sm'
                  value={medication.dosage || ''}
                  onChange={(e) =>
                    handleChange(`policyLifeMedications[${index}].dosage`, e.target.value)
                  }
                />
              </td>
              <td>
                <Form.Control
                  size='sm'
                  value={medication.frequency || ''}
                  onChange={(e) =>
                    handleChange(`policyLifeMedications[${index}].frequency`, e.target.value)
                  }
                />
              </td>
              <td>
                <Form.Control
                  size='sm'
                  value={medication.diagnosis || ''}
                  onChange={(e) =>
                    handleChange(`policyLifeMedications[${index}].diagnosis`, e.target.value)
                  }
                />
              </td>
              <td className='text-center'>
                <Button
                  size='sm'
                  variant='light-danger'
                  onClick={() => removeMedication(index)}
                  title={intl.formatMessage({id: 'common.delete', defaultMessage: 'Delete'})}
                >
                  <i className='bi bi-trash' />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Button size='sm' variant='light-primary' onClick={addMedication}>
        <i className='bi bi-plus-lg me-1' />
        <FormattedMessage
          id='policies.detail.form.life.addMedication'
          defaultMessage='Add medication'
        />
      </Button>

      <Row className='mt-4'>
        <Col md={12}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.detail.form.life.medicalConditions'
                defaultMessage='Medical conditions'
              />
            </Form.Label>
            <Form.Control
              as='textarea'
              rows={3}
              value={life?.medicalConditions || ''}
              onChange={(e) => onLife('medicalConditions', e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Tabaco: el sí/no es del holder, el detalle es de la póliza. */}
      <div className='kt-heading kt-heading--md custom-border-brand mt-5'>
        <FormattedMessage
          id='policies.detail.form.life.tobaccoHeading'
          defaultMessage='Tobacco or nicotine use'
        />
      </div>
      <Row>
        <Col md={12}>
          <Form.Check
            type='switch'
            id='life-uses-tobacco'
            checked={usesTobacco}
            onChange={(e) => handleChange('holder.usesTobacco', e.target.checked)}
            label={intl.formatMessage({
              id: 'policies.detail.form.life.usesTobacco',
              defaultMessage:
                'Has used any product containing tobacco or nicotine within the last five years',
            })}
          />
        </Col>
      </Row>
      {usesTobacco && (
        <Row className='mt-3'>
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='policies.detail.form.life.tobaccoProductType'
                  defaultMessage='Product type'
                />
              </Form.Label>
              <Form.Control
                value={life?.tobaccoProductType || ''}
                onChange={(e) => onLife('tobaccoProductType', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='policies.detail.form.life.tobaccoFrequency'
                  defaultMessage='Frequency'
                />
              </Form.Label>
              <Form.Control
                value={life?.tobaccoFrequency || ''}
                onChange={(e) => onLife('tobaccoFrequency', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className='mt-2'>
              <DateInput
                id='tobaccoLastUsedDate'
                name='tobaccoLastUsedDate'
                label={intl.formatMessage({
                  id: 'policies.detail.form.life.tobaccoLastUsedDate',
                  defaultMessage: 'Date last used',
                })}
                value={life?.tobaccoLastUsedDate || ''}
                onChange={onLife}
                onBlur={onLifeBlur}
              />
            </Form.Group>
          </Col>
        </Row>
      )}

      <div className='kt-heading kt-heading--md custom-border-brand mt-5'>
        <FormattedMessage
          id='policies.detail.form.life.physicianHeading'
          defaultMessage='Personal physician'
        />
      </div>
      <Row>
        <Col md={4}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.detail.form.life.physicianName'
                defaultMessage='Physician name'
              />
            </Form.Label>
            <Form.Control
              value={life?.physicianName || ''}
              onChange={(e) => onLife('physicianName', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className='mt-2'>
            <DateInput
              id='physicianLastVisitDate'
              name='physicianLastVisitDate'
              label={intl.formatMessage({
                id: 'policies.detail.form.life.physicianLastVisitDate',
                defaultMessage: 'Date of last visit',
              })}
              value={life?.physicianLastVisitDate || ''}
              onChange={onLife}
              onBlur={onLifeBlur}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.detail.form.life.physicianAddress'
                defaultMessage='Physician address'
              />
            </Form.Label>
            <Form.Control
              value={life?.physicianAddress || ''}
              onChange={(e) => onLife('physicianAddress', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.detail.form.life.physicianLastVisitReason'
                defaultMessage='Reason for last visit'
              />
            </Form.Label>
            <Form.Control
              as='textarea'
              rows={2}
              value={life?.physicianLastVisitReason || ''}
              onChange={(e) => onLife('physicianLastVisitReason', e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      <div className='kt-heading kt-heading--md custom-border-brand mt-5'>
        <FormattedMessage
          id='policies.detail.form.life.familyHistoryHeading'
          defaultMessage='Family medical history'
        />
      </div>
      {(['father', 'mother'] as const).map((parent) => {
        const living = life?.[`${parent}Living` as const] === true
        return (
          <Row key={parent} className='mb-3 align-items-end'>
            <Col md={3}>
              <Form.Check
                type='switch'
                id={`life-${parent}-living`}
                checked={living}
                onChange={(e) => onLife(`${parent}Living`, e.target.checked)}
                label={
                  parent === 'father'
                    ? intl.formatMessage({
                        id: 'policies.detail.form.life.fatherLiving',
                        defaultMessage: 'Father living',
                      })
                    : intl.formatMessage({
                        id: 'policies.detail.form.life.motherLiving',
                        defaultMessage: 'Mother living',
                      })
                }
              />
            </Col>
            {living ? (
              <Col md={3}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage id='policies.detail.form.life.age' defaultMessage='Age' />
                  </Form.Label>
                  <Form.Control
                    type='number'
                    value={life?.[`${parent}Age` as const] ?? ''}
                    onChange={(e) => onLife(`${parent}Age`, e.target.value)}
                  />
                </Form.Group>
              </Col>
            ) : (
              <>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      <FormattedMessage
                        id='policies.detail.form.life.ageDeath'
                        defaultMessage='Age at death'
                      />
                    </Form.Label>
                    <Form.Control
                      type='number'
                      value={life?.[`${parent}AgeDeath` as const] ?? ''}
                      onChange={(e) => onLife(`${parent}AgeDeath`, e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      <FormattedMessage
                        id='policies.detail.form.life.deathCause'
                        defaultMessage='Cause'
                      />
                    </Form.Label>
                    <Form.Control
                      value={life?.[`${parent}DeathCause` as const] || ''}
                      onChange={(e) => onLife(`${parent}DeathCause`, e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </>
            )}
          </Row>
        )
      })}

      <div className='btn-prev-next'>
        <Button size='sm' variant='secondary' onClick={onPrevStep}>
          <FormattedMessage id='policies.list.wizard.previous' defaultMessage='PREVIOUS' />
        </Button>
        <Button size='sm' onClick={onNextStep}>
          <FormattedMessage id='policies.list.wizard.nextStep' defaultMessage='NEXT STEP' />
        </Button>
      </div>
    </div>
  )
}
